import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type {
  ProjectDetailDto,
  ProjectMember,
  ProjectMemberSource,
  ProjectRole,
} from '@ledokol/contracts'
import {
  addProjectMember,
  listProjectMembers,
  removeProjectMember,
  syncProjectMembersFromTg,
  updateProjectMember,
} from '../../shared/api/project-members'
import { fetchUsers } from '../../shared/api/users'
import { useAuthStore } from '../auth/store'

interface Props {
  project: ProjectDetailDto
}

// Список enum-ролей с человеческими названиями. Цвета — для бейджа.
const ROLE_OPTS: { value: ProjectRole; label: string; color: string }[] = [
  { value: 'account', label: 'Account', color: 'blue' },
  { value: 'manager', label: 'Manager', color: 'grape' },
  { value: 'specialist', label: 'Specialist', color: 'teal' },
  { value: 'client', label: 'Клиент', color: 'orange' },
  { value: 'observer', label: 'Наблюдатель', color: 'gray' },
]
const ROLE_LABEL: Record<ProjectRole, string> = Object.fromEntries(
  ROLE_OPTS.map((r) => [r.value, r.label]),
) as Record<ProjectRole, string>
const ROLE_COLOR: Record<ProjectRole, string> = Object.fromEntries(
  ROLE_OPTS.map((r) => [r.value, r.color]),
) as Record<ProjectRole, string>

const SOURCE_LABEL: Record<ProjectMemberSource, { label: string; color: string }> = {
  tg_join: { label: 'TG · вступил', color: 'cyan' },
  tg_message: { label: 'TG · сообщение', color: 'cyan' },
  manual: { label: 'Вручную', color: 'gray' },
}

export function MembersTab({ project }: Props) {
  const qc = useQueryClient()
  const { user: me } = useAuthStore()

  // RBAC на UI — зеркалит ProjectsService.assertCanManageMembers:
  // admin/director — всегда; head — если департамент проекта совпадает;
  // account-lead того же проекта — да; остальные → read-only.
  const projectDeptIds = useMemo(
    () => new Set(project.departments.map((d) => d.id)),
    [project],
  )
  const canManage =
    me?.role === 'admin' ||
    me?.role === 'director' ||
    (me?.role === 'head' && me.departments.some((d) => projectDeptIds.has(d.id))) ||
    (me?.role === 'account' && project.lead.id === me.id)

  const membersQ = useQuery({
    queryKey: ['project-members', project.id],
    queryFn: () => listProjectMembers(project.id),
  })

  // Кандидаты на ручное добавление — все активные не-удалённые сотрудники.
  // Не сужаем по роли: продуктовое решение, кто становится участником.
  const usersQ = useQuery({
    queryKey: ['users', { active: true }],
    queryFn: () => fetchUsers({ active: true }),
    enabled: canManage,
  })

  // ── Drawer добавления ────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false)
  const [pickedUserId, setPickedUserId] = useState<string | null>(null)
  const [pickedRole, setPickedRole] = useState<ProjectRole | null>(null)
  const [pickedNote, setPickedNote] = useState('')

  useEffect(() => {
    if (!addOpen) {
      setPickedUserId(null)
      setPickedRole(null)
      setPickedNote('')
    }
  }, [addOpen])

  const addMut = useMutation({
    mutationFn: () =>
      addProjectMember(project.id, {
        user_id: pickedUserId!,
        project_role: pickedRole,
        role_in_project: pickedNote.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({ message: 'Участник добавлен', color: 'green' })
      setAddOpen(false)
      qc.invalidateQueries({ queryKey: ['project-members', project.id] })
      qc.invalidateQueries({ queryKey: ['project', project.id] })
    },
    onError: (e: unknown) => {
      const msg = extractError(e)
      notifications.show({ message: msg, color: 'red' })
    },
  })

  // ── Drawer редактирования (роль + подпись) ───────────────────────────────
  const [editing, setEditing] = useState<ProjectMember | null>(null)
  const [editRole, setEditRole] = useState<ProjectRole | null>(null)
  const [editNote, setEditNote] = useState('')

  useEffect(() => {
    if (editing) {
      setEditRole(editing.project_role)
      setEditNote(editing.role_in_project ?? '')
    }
  }, [editing])

  const editMut = useMutation({
    mutationFn: () =>
      updateProjectMember(project.id, editing!.user.id, {
        project_role: editRole,
        role_in_project: editNote.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({ message: 'Роль обновлена', color: 'green' })
      setEditing(null)
      qc.invalidateQueries({ queryKey: ['project-members', project.id] })
    },
    onError: (e: unknown) => {
      notifications.show({ message: extractError(e), color: 'red' })
    },
  })

  const removeMut = useMutation({
    mutationFn: (userId: string) => removeProjectMember(project.id, userId),
    onSuccess: () => {
      notifications.show({ message: 'Участник удалён', color: 'green' })
      qc.invalidateQueries({ queryKey: ['project-members', project.id] })
      qc.invalidateQueries({ queryKey: ['project', project.id] })
    },
    onError: (e: unknown) => {
      notifications.show({ message: extractError(e), color: 'red' })
    },
  })

  // Синхронизация с TG: пробежать по архивным draft'ам, для каждого TG-id
  // findOrCreateStub + ensureMember. Для тех, кто писал в чате, но не попал
  // в команду из-за расхождения в логике до фикса.
  const syncMut = useMutation({
    mutationFn: () => syncProjectMembersFromTg(project.id),
    onSuccess: (r) => {
      notifications.show({
        message: `Готово. Добавлено: ${r.ensured} · стабов создано: ${r.stubsCreated}`,
        color: 'green',
      })
      qc.invalidateQueries({ queryKey: ['project-members', project.id] })
      qc.invalidateQueries({ queryKey: ['project', project.id] })
    },
    onError: (e: unknown) => {
      notifications.show({ message: extractError(e), color: 'red' })
    },
  })

  // Для Select добавления — исключаем тех, кто уже активный член (leftAt=null).
  // Вышедших оставляем, чтобы admin мог «вернуть» (бэкенд сбросит leftAt и source=manual).
  const userOptions = useMemo(() => {
    const activeMemberIds = new Set(
      (membersQ.data ?? []).filter((m) => m.left_at === null).map((m) => m.user.id),
    )
    return (usersQ.data ?? [])
      .filter((u) => !activeMemberIds.has(u.id))
      .map((u) => ({
        value: u.id,
        label: u.tg_username ? `${u.full_name} — @${u.tg_username}` : u.full_name,
      }))
  }, [usersQ.data, membersQ.data])

  if (membersQ.isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  const rows = membersQ.data ?? []
  const active = rows.filter((m) => m.left_at === null)
  const left = rows.filter((m) => m.left_at !== null)

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          Команда проекта. Участники автоматически попадают сюда, когда вступают в TG-группу
          или пишут первое сообщение. Роли назначает admin/директор/head/лид.
        </Text>
        {canManage && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconRefresh size={16} />}
              onClick={() => syncMut.mutate()}
              loading={syncMut.isPending}
              title="Подтянуть участников из TG-чата по архивным сообщениям"
            >
              Синхронизировать с TG
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setAddOpen(true)}
              disabled={userOptions.length === 0 && (usersQ.data?.length ?? 0) > 0}
            >
              Добавить участника
            </Button>
          </Group>
        )}
      </Group>

      {rows.length === 0 ? (
        <Center mih={100}>
          <Text c="dimmed">Участников пока нет</Text>
        </Center>
      ) : (
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Сотрудник</Table.Th>
              <Table.Th>Роль</Table.Th>
              <Table.Th>Подпись</Table.Th>
              <Table.Th>Источник</Table.Th>
              <Table.Th>Добавлен</Table.Th>
              {canManage && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[...active, ...left].map((m) => (
              <Table.Tr
                key={m.user.id}
                style={m.left_at ? { opacity: 0.55 } : undefined}
              >
                <Table.Td>
                  <Group gap="sm" wrap="nowrap">
                    <Avatar src={m.user.avatar_url} size="sm" radius="xl">
                      {m.user.full_name.slice(0, 1)}
                    </Avatar>
                    <Stack gap={0}>
                      <Text size="sm">{m.user.full_name}</Text>
                      {m.left_at && (
                        <Badge size="xs" variant="light" color="red">
                          вышел {dayjs(m.left_at).format('D MMM')}
                        </Badge>
                      )}
                    </Stack>
                  </Group>
                </Table.Td>
                <Table.Td>
                  {m.project_role ? (
                    <Badge color={ROLE_COLOR[m.project_role]} variant="light">
                      {ROLE_LABEL[m.project_role]}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{m.role_in_project ?? '—'}</Text>
                </Table.Td>
                <Table.Td>
                  {m.source ? (
                    <Badge color={SOURCE_LABEL[m.source].color} variant="dot" size="sm">
                      {SOURCE_LABEL[m.source].label}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{dayjs(m.added_at).format('D MMM YYYY')}</Text>
                </Table.Td>
                {canManage && (
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => setEditing(m)}
                        title="Изменить роль"
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={m.user.id === project.lead.id}
                        onClick={() => {
                          if (
                            confirm(
                              `Убрать «${m.user.full_name}» из участников проекта? Историю и FK в задачах это НЕ затронет.`,
                            )
                          ) {
                            removeMut.mutate(m.user.id)
                          }
                        }}
                        title={
                          m.user.id === project.lead.id
                            ? 'Лида нельзя удалить из участников'
                            : 'Удалить'
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* ── Drawer добавления ───────────────────────────────────────────── */}
      <Drawer
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title="Добавить участника"
        position="right"
        size="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Поиск по ФИО или @telegram-нику среди активных сотрудников.
          </Text>
          <Select
            label="Сотрудник"
            placeholder="Выберите…"
            data={userOptions}
            value={pickedUserId}
            onChange={setPickedUserId}
            searchable
            nothingFoundMessage={usersQ.isLoading ? 'Загрузка…' : 'Не найдено'}
            required
          />
          <Select
            label="Роль в проекте"
            placeholder="(можно позже)"
            data={ROLE_OPTS.map((r) => ({ value: r.value, label: r.label }))}
            value={pickedRole}
            onChange={(v) => setPickedRole(v as ProjectRole | null)}
            clearable
          />
          <TextInput
            label="Подпись (свободно)"
            placeholder="Performance manager / Brand Lead"
            value={pickedNote}
            onChange={(e) => setPickedNote(e.currentTarget.value)}
          />
          <Divider />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddOpen(false)}>
              Отмена
            </Button>
            <Button
              loading={addMut.isPending}
              disabled={!pickedUserId}
              onClick={() => addMut.mutate()}
            >
              Добавить
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Drawer редактирования ───────────────────────────────────────── */}
      <Drawer
        opened={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Роль: ${editing.user.full_name}` : ''}
        position="right"
        size="md"
      >
        {editing && (
          <Stack>
            <Select
              label="Роль в проекте"
              data={ROLE_OPTS.map((r) => ({ value: r.value, label: r.label }))}
              value={editRole}
              onChange={(v) => setEditRole(v as ProjectRole | null)}
              clearable
            />
            <TextInput
              label="Подпись (свободно)"
              value={editNote}
              onChange={(e) => setEditNote(e.currentTarget.value)}
              placeholder="Performance manager / Brand Lead"
            />
            <Divider />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setEditing(null)}>
                Отмена
              </Button>
              <Button loading={editMut.isPending} onClick={() => editMut.mutate()}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>

      <Box mt="md">
        <Text size="xs" c="dimmed">
          Всего: {rows.length} · активных: {active.length} · вышедших: {left.length}
        </Text>
      </Box>
    </Stack>
  )
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } }
  return err?.response?.data?.error?.message ?? err?.response?.data?.error?.code ?? 'Ошибка'
}
